<script lang="ts">
import { computed, defineComponent, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { usePhoneStore } from '@/stores/phoneStore'
import LogoForApp from '@/components/LogoForApp.vue'
import { useLangStore } from '@/stores/langStore'
import { useThemeStore } from '@/stores/darkThemeStore'

export default defineComponent({
  name: 'App',
  components: {
    LogoForApp,
  },
  setup() {
    const { locale } = useI18n()
    const route = useRoute()
    const langStore = useLangStore()
    const themeStore = useThemeStore()

    const numberReplacementRegex = /^\/[a-z]{2}\/number-replacement$/
    const numberReplacementPaymentRegex
      = /^\/[a-z]{2}\/number-replacement-payment$/

    const isHomePage = computed(() => {
      return (
        route.path !== '/beelive/scanner'
        && route.path !== '/beedoc/scanner'
        && !numberReplacementRegex.test(route.path)
        && !numberReplacementPaymentRegex.test(route.path)
      )
    })

    const isOtherPage = computed(() => {
      return (
        route.path === '/beelive/scanner' || route.path === '/beedoc/scanner'
      )
    })

    const isNumberReplacement = computed(() => {
      return (
        numberReplacementRegex.test(route.path)
        || numberReplacementPaymentRegex.test(route.path)
      )
    })

    const isLogoVisible = computed(() => {
      return (
        !numberReplacementRegex.test(route.path)
        && !numberReplacementPaymentRegex.test(route.path)
      )
    })

    const ctnFormatted = (ctn: string) => {
      return ctn.replace(/\s+/g, '')
    }

    const setPhone = (ctn: string) => {
      const ctn_formatted = ctnFormatted(ctn)
      const phoneStore = usePhoneStore()
      phoneStore.setCtn(ctn_formatted)
    }

    onMounted(() => {
      const urlParams = new URLSearchParams(window.location.search)
      const ctn = urlParams.get('ctn')

      if (route.path === '/' && ctn && ctn.length >= 10) {
        setPhone(ctn)
      }
    })

    watch(
      () => langStore.lang,
      (newLang) => {
        locale.value = newLang
      },
      { immediate: true },
    )

    watch(
      () => themeStore.theme,
      (newTheme) => {
        themeStore.theme = newTheme
      },
      { immediate: true },
    )

    return {
      isHomePage,
      isOtherPage,
      isNumberReplacement,
      isLogoVisible,
      themeStore,
    }
  },
})
</script>

<template>
  <div
    :class="{
      'home-page': isHomePage,
      'other-page': isOtherPage,
      'number-replacement': isNumberReplacement,
      'dark-mode': isNumberReplacement && themeStore.theme === 'dark',
    }"
  >
    <LogoForApp v-if="isLogoVisible" />
    <router-view />
  </div>
</template>

<style lang="scss">
#app {
  min-height: 100vh;
  height: 100%;
}
.other-page {
  min-height: 100vh;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #f8f8f8;
  align-items: center;
}
.home-page {
  display: flex;
  flex-direction: column;
  background: #f8f8f8;
  align-items: center;
  justify-content: flex-start;
  padding: 100px 16px 0 16px;
  gap: 48px;
  min-height: 100vh;
  height: 100%;
}

.number-replacement {
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 8px 16px;
  min-height: 100vh;
  height: 100%;
}

.dark-mode {
  background: var(--dark-background-color);
}

@media screen and (max-width: 500px) {
  .home-page {
    background: #ffffff;
    padding: 64px 16px 16px 16px;
  }
}
@media screen and (min-height: 650px) {
  .home-page {
    padding: 100px 16px 16px 16px;
  }
}
</style>
